<!--
  - Copyright (c) 2018 BarD Software s.r.o
  -->

<template>
    <div class="project-new-modal modal fade" tabindex="-1" @keydown="applyShortcut">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header flex-column">
                    <div class="d-flex w-100">
                        <h4 class="modal-title flex-fill">New Project</h4>
                        <button type="button" class="close" aria-label="Close" v-on:click="reset">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="d-flex">
                        <div v-if="step === WizardStep.RESOURCES" class="w-100">
                            <p class="help-text">Choose resources of your project.</p>
                        </div>
                        <div v-if="step === WizardStep.GENERAL_PROPS" class="w-100">
                            <p class="help-text">You can share files on GanttProject Cloud from desktop GanttProject application, by uploading from your browser or by creating new project from template.</p>
                        </div>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="step === WizardStep.GENERAL_PROPS">
                        <div class="m-2 my-3 form-group">
                            <label class="project-menu-label">Project filename</label>
                            <input class="form-control" type="text" v-model="projectFilename">
                        </div>
                        <div class="m-2">
                            <div class="my-2 form-check">
                                <input id="template-project-radio"
                                       class="my-1 form-check-input"
                                       type="radio" name="new-project-type"
                                       value="#template-project-menu"
                                       v-model="selectedMenu"
                                       checked>
                                <label class="project-menu-label form-check-label" for="template-project-radio">From template</label>
                            </div>
                            <div id="template-project-menu" class="my-2 new-project-menu">
                                <select id="template-project-select" class="form-control custom-select"
                                        v-model="selectedTemplateId"
                                        v-on:click="onSelectedTemplateId">
                                    <option id="tpl-builtin-0" value="tpl-builtin-0" selected>Empty Project</option>
                                    <option id="tpl-builtin-1" value="tpl-builtin-1">House Building Sample</option>
                                </select>
                            </div>
                        </div>

                        <div class="m-2">
                            <div class="my-2 form-check">
                                <input id="upload-project-radio"
                                       class="form-check-input"
                                       type="radio" name="new-project-type"
                                       value="#upload-project-menu"
                                       v-model="selectedMenu">
                                <label class="project-menu-label form-check-label" for="upload-project-radio">Upload</label>
                            </div>

                            <div id="upload-project-menu" class="my-1 new-project-menu">
                                <div class="custom-file">
                                    <input id="project-file-input"
                                           class="custom-file-input" type="file" accept=".gan"
                                           v-on:change="onFileSelected"
                                           disabled>
                                    <label id="project-file-label" class="custom-file-label">{{ projectFileLabel }}</label>
                                    <div class="invalid-feedback"> Please choose a file with <code>.gan</code> extension.</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="step === WizardStep.RESOURCES">
                        <table class="table table-hover m-2 my-2">
                            <tbody class="scrollable">
                            <tr class="w-100" v-for="(r, i) in resources" @click="toggleResource(`checkbox-${i}`)">
                                <td class="centered-checkbox" :class="i === 0 ? 'borderless-top' : ''">
                                    <input v-bind="{'id': `checkbox-${i}`}" type="checkbox" :value="r" v-on:click.stop v-model="selectedResources">
                                </td>
                                <td :class="i === 0 ? 'borderless-top' : ''">
                                    <resource-item v-bind:resource="r"/>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="modal-footer d-flex justify-content-end">
                    <button class="btn btn-link" type="button"
                            :disabled="step === WizardStep.GENERAL_PROPS"
                            @click="setStep(WizardStep.GENERAL_PROPS)">Back</button>
                    <button class="btn btn-link" type="button"
                            :disabled="step === WizardStep.RESOURCES"
                            @click="setStep(WizardStep.RESOURCES)">Next</button>
                    <BtnSubmit :title="submitButtonTitle" spinner ref="submitButton" :submit-handler="onSubmit"></BtnSubmit>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch, Ref} from 'vue-facing-decorator';
import {updateDependencies} from '../Dashboard';
import {loadTeam, ResourceDto, TeamDto} from '../Teams';
import {handleXhrError, ShortcutHandler} from '../utils/Ui';
import {ProjectCreate, ProjectUpload} from '../ProjectRoutes';
import BtnSubmit from './BtnSubmit.vue';
import {TeamResourcesList} from '../Routes';
import {Deferred} from 'jquery';

import $ from 'jquery';

type SelectedMenu = '#template-project-menu' | '#upload-project-menu';
type SubmitButtonTitle = 'Create' | 'Upload';
type TemplateId = 'tpl-builtin-0' | 'tpl-builtin-1';
enum WizardStep {
  GENERAL_PROPS, RESOURCES,
}

const SELECTED_MENU_DEFAULT = '#template-project-menu';
const SELECTED_TEMPLATE_ID_DEFAULT = 'tpl-builtin-0';
const PROJECT_FILENAME_DEFAULT = 'EmptyProject.gan';
const SUBMIT_BUTTON_DEFAULT = 'Create';
const PROJECT_FILE_LABEL_DEFAULT = 'Choose project file';

@Component({
  components: {BtnSubmit},
})
export default class ProjectNewModal extends Vue {

  public projectFilename: string = PROJECT_FILENAME_DEFAULT;

  public selectedMenu: SelectedMenu = SELECTED_MENU_DEFAULT;

  public submitButtonTitle: SubmitButtonTitle = SUBMIT_BUTTON_DEFAULT;

  public selectedTemplateId: TemplateId = SELECTED_TEMPLATE_ID_DEFAULT;

  public projectFileLabel: string = PROJECT_FILE_LABEL_DEFAULT;

  @Prop()
  public team: TeamDto;

  @Ref()
  public readonly submitButton!: BtnSubmit;
  private WizardStep: any = WizardStep;
  private step: WizardStep = WizardStep.GENERAL_PROPS;
  private selectedResources: ResourceDto[] = [];
  private resources: ResourceDto[] = [];
  private shortcutHandler = new ShortcutHandler();
  private deferredFileContent: JQueryDeferred<string> = Deferred<string>();

  public constructor() {
    super();
    this.shortcutHandler.register('Enter', () => this.onSubmit());
  }

  public mounted() {
    this.reset();
    $(this.$el).on('shown.bs.modal', () => this.loadResources());
    // this.submitButton.submitHandler = () => this.onSubmit();
  }

  @Watch('selectedMenu')
  public watchSelectedMenu(val: string) {
    $('.new-project-menu').find('select,input').attr('disabled', '');
    $(val).find('select,input').removeAttr('disabled');

    if (val === '#template-project-menu') {
      this.selectedTemplateId = SELECTED_TEMPLATE_ID_DEFAULT;
      this.onSelectedTemplateId();
      this.submitButtonTitle = 'Create';
    } else {
      const projectFileInput = getInputFile('project-file-input');
      this.projectFilename = projectFileInput ? projectFileInput.name : '';
      this.submitButtonTitle = 'Upload';
    }
  }

  public onSelectedTemplateId() {
    const templatename = getElement<HTMLOptionElement>(this.selectedTemplateId).label;
    this.projectFilename = templatename.replace(/\s/g, '') + '.gan';
  }

  public onSubmit(): JQueryPromise<any> {
    const teamRefid = this.team.refid;
    const promise = this.isTemplateSelected() ? this.createProject(teamRefid) : this.uploadProject(teamRefid);

    if (!promise) {
      return;
    }

    return promise.then(() => loadTeam(teamRefid))
        .then(updateDependencies)
        .then(() => this.reset())
        .fail((jqXhr) => handleXhrError(jqXhr, 'Failed to create a new project.'));
  }

  public onFileSelected() {
    const file = getInputFile('project-file-input');
    this.projectFilename = file!.name;
    this.projectFileLabel = file!.name;

    const reader = new FileReader();
    reader.onload = () => { this.deferredFileContent.resolve(reader.result as string); };
    reader.readAsDataURL(file);
  }

  public applyShortcut(e: KeyboardEvent) {
    this.shortcutHandler.handle(e);
  }

  private createProject(teamRefid: string): JQueryPromise<any> {
    return ProjectCreate.invoke({
      teamRefid,
      filename: this.projectFilename,
      templateId: this.selectedTemplateId,
      resources: this.selectedResources,
    });
  }

  private showInvalidFileInputMessage() {
    const input = $('#project-file-input');
    input.addClass('is-invalid');
    $(this.$el).one('change', () => input.removeClass('is-invalid'));
    $(this.$el).one('hidden.bs.modal', () => input.removeClass('is-invalid'));
  }

  private uploadProject(teamRefid: string): JQueryPromise<any> | undefined {
    const file = getInputFile('project-file-input');

    if (!file) {
      this.showInvalidFileInputMessage();
      return;
    }

    return this.deferredFileContent.then((fileContent) =>
        ProjectUpload.invoke({
          teamRefid,
          filename: this.projectFilename,
          encodedContent: fileContent.split(',')[1],
        }),
    );
  }

  private reset() {
    this.selectedMenu = SELECTED_MENU_DEFAULT;
    this.projectFileLabel = PROJECT_FILE_LABEL_DEFAULT;
    this.selectedTemplateId = SELECTED_TEMPLATE_ID_DEFAULT;
    this.step = WizardStep.GENERAL_PROPS;
    this.selectedResources = [];
    this.resources = [];
    this.deferredFileContent = Deferred<string>();
    $(this.$el).find('#project-file-input').val('');
    $(this.$el).modal('hide');
  }

  private isTemplateSelected(): boolean {
    return this.selectedMenu === '#template-project-menu';
  }

  private loadResources() {
    TeamResourcesList.invoke({ teamRefid: this.team.refid })
        .done((resources: ResourceDto[]) => { this.resources = resources; })
        .fail((jqXhr) => handleXhrError(jqXhr, 'can\'t load possible resources'));
  }

  private toggleResource(id: string) {
    $(`#${id}`).trigger('click');
  }

  private setStep(step: WizardStep) {
    this.step = step;
  }
}

function getElement<T extends HTMLElement>(id): T {
  return document.getElementById(id) as T;
}

function getInputFile(id: string): File | undefined {
  return getElement<HTMLInputElement>(id).files![0];
}
</script>

<style lang="scss" scoped>
    .modal-header {
        flex-wrap: wrap;
        .help-text {
            margin-bottom: 0;
        }
    }

    .modal-body {
        padding-top: 0;
    }

    .borderless-top {
        border-top: none !important;
    }
</style>
